<template>
  <main v-if="person">
    <v-row class="bg-primary">
      <v-col class="v-col-12 v-col-md-3 pb-0">
        <v-img
            aspect-ratio="1/1"
            :srcset="person.content.profile_picture.filename + '/m/300x300 300w,' + person.content.profile_picture.filename + '/m/480x480 480w,' + person.content.profile_picture.filename + '/m/640x640 640w,' + person.content.profile_picture.filename + '/m/960x960 960w,'  + person.content.profile_picture.filename + '/m/1280x1280 1280w'"
            sizes="(max-width: 600px) 300px, (min-width: 600px) 480px, (min-width: 960px) 640px, (min-width: 1280px) 960px, 1280px"
            :src="person.content.profile_picture.filename + '/m/300x300'"
            :alt="person.name"
            cover
        />
      </v-col>
      <v-col class="v-col-12 v-col-md-9 d-flex flex-column justify-center">
        <v-container fluid class="people-name">
          <h1 class="mb-6">{{ person.name }}</h1>
          <h2>{{ person.content.role }}</h2>
          <div class="d-flex flex-row mt-6">
            <a v-if="person.content.twitter" :href="'https://twitter.com/' + person.content.twitter" title="x (formally Twitter)"><v-icon size="40" class="mr-2" icon="fa-brands fa-square-x-twitter"></v-icon></a>
            <a v-if="person.content.linkedin" :href="'https://www.linkedin.com/in/' + person.content.linkedin" title="LinkedIn"><v-icon size="40" class="mr-2" icon="fa-brands fa-linkedin"></v-icon></a>
            <a v-if="person.content.instagram" :href="'https://www.instagram.com/' + person.content.instagram" title="Instagram"><v-icon size="40" class="mr-2" icon="fa-brands fa-instagram"></v-icon></a>
          </div>
          <p class="mt-3"><a :href="'mailto:' + person.content.email">{{ person.content.email }}</a></p>
        </v-container>
      </v-col>
    </v-row>

    <v-container fluid class="body-content">
      <v-row>
        <v-col class="v-col-12 v-col-md-10 offset-md-1 v-col-lg-6 offset-lg-3">
          <p>{{ person.content.description }}</p>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid>
      <h2 class="text-center mt-16 mb-6">Latest</h2>
      <v-divider color="black" style="opacity:1" class="mb-6"></v-divider>
      <component v-if="stories" is="story-list" :blok="stories"></component>
      <div class="text-center">
        <v-container>
          <v-row justify="center">
            <v-col cols="8">
              <v-container class="max-width">
                <v-pagination
                    v-model="page"
                    :length="total"
                    class="my-4"
                ></v-pagination>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-container>

    <foot />

    <teleport to="head">
      <component :is="'script'" type="application/ld+json">
        {{ jsonLd }}
      </component>
    </teleport>
  </main>
</template>

<script setup>

import axios from "axios";

const props = defineProps({
  slug: String
})

import {computed, onMounted, watch} from "vue";

import { ref } from 'vue'
import router from "@/router";
import {useSeoMeta} from "@unhead/vue";
const person = ref(null)
const stories = ref(null)
const page = ref(1)
const total = ref(0)

onMounted(() => {

  window.scrollTo(0,0)

  let apiModes = import.meta.env.VITE_API_MODES.split(",")

  if (apiModes.includes(import.meta.env.MODE) && !window.location.search.includes('_storyblok')) {
    loadPerson(1);
  }

})

watch(page, () => {
  loadPerson(page.value);
});

import Foot from "@/components/Foot.vue";
import {trackingService} from "@/services/tracking-service";

const loadPerson = (page) => {

  const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_HOST
  });

  apiClient
      .get('/api/people/' + props.slug + '?page=' + page)
      .then(response => {
        person.value = response.data.person
        stories.value = response.data.stories
        total.value = Math.ceil(response.data.total / response.data.per_page)

        // https://unhead.unjs.io/usage/composables/use-seo-meta
        useSeoMeta({
          title: 'Digital Frontier | Author: ' + person.value.name,
          description: person.value.content.role,
          ogDescription: person.value.content.role,
          ogTitle: 'Digital Frontier | Author: ' + person.value.name,
          ogImage: person.value.content.profile_picture.filename + '/m/640x640',
          ogType: "website",
          ogUrl: "https://digitalfrontier.com/author/" + state.story.slug,
          twitterCard: 'summary_large_image',
        })

        if (trackingService.isGtagEnabled()) {
          gtag('event', 'page_view', {
            page_title: 'Digital Frontier | Author: ' + person.value.name,
            page_location: "https://digitalfrontier.com/author/" + state.story.slug,
            page_path: "/author/" + state.story.slug,
          });
        }

      })
      .catch(error => {
        console.log(error)

        if (error.response && error.response.status === 404) {
          router.push({name: '404'})
        }
      }).finally(() => {
        setTimeout(function(){
          window.prerenderReady = true
        },1000);
      })
}

const jsonLd = computed(() => {
  if (person) {
    return {
      "@context": "http://schema.org",
      "@type": "Person",
      "@id": "https://digitalfrontier.com/authors/" + person.value.slug,
      "image": person.value.content.profile_picture.filename + '/m/300/300',
      "jobTitle": person.value.content.role,
      "name": person.value.name,
      "url": "https://digitalfrontier.com",
      "description": person.value.content.description
    }
  }
  return {}
})

</script>

<style scoped lang="scss">
  h1 {
    font-size: 4em;
    line-height: 1em;
  }
  h2 {
    font-family: "DepartureMonoRegular", sans-serif !important;
    font-weight: normal;
    font-size: 2.1em;
    line-height: 1em;
  }
  @media only screen and (min-width: 600px) {
    .people-name {
      padding: 0 80px;
    }
  }

  .body-content {
    margin-top: 80px;
    margin-bottom: 60px;
    p {
      // font-family: "SaansMonoRegular", sans-serif !important;
      //font-size: 1.625em;
      @media only screen and (min-width: 600px) {
        padding-left: 68px;
      }
    }
    @media only screen and (max-width: 599px) {
      margin-top: 20px;
      margin-bottom: 10px;
    }

  }
</style>
